import React, { useState, useEffect } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import { cn } from "@/lib/utils";

interface RankingItem {
  id: string;
  label: string;
}

interface DraggableRankingListProps {
  items: RankingItem[];
  value?: string[]; // Array of ids in current order
  onChange: (orderedIds: string[]) => void;
}

interface SortableItemProps {
  item: RankingItem;
  index: number;
}

function SortableItem({ item, index }: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center gap-3 p-3 bg-muted/50 rounded-lg transition-all",
        isDragging && "shadow-lg bg-background ring-2 ring-primary/20 z-10 opacity-95",
        !isDragging && "hover:bg-muted"
      )}
    >
      <button
        {...attributes}
        {...listeners}
        className="touch-none p-1 rounded hover:bg-muted-foreground/10 cursor-grab active:cursor-grabbing"
        aria-label="Ziehen zum Sortieren"
      >
        <GripVertical className="h-4 w-4 text-muted-foreground" />
      </button>
      <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-sm flex items-center justify-center font-medium flex-shrink-0">
        {index + 1}
      </span>
      <span className="flex-1 text-sm">{item.label}</span>
    </div>
  );
}

export function DraggableRankingList({ items, value, onChange }: DraggableRankingListProps) {
  // Initialize with value order or default item order
  const [orderedItems, setOrderedItems] = useState<RankingItem[]>(() => {
    if (value && value.length > 0) {
      // Sort items based on value order
      const ordered: RankingItem[] = [];
      value.forEach(id => {
        const item = items.find(i => i.id === id);
        if (item) ordered.push(item);
      });
      // Add any items not in value at the end
      items.forEach(item => {
        if (!value.includes(item.id)) {
          ordered.push(item);
        }
      });
      return ordered;
    }
    return items;
  });

  // Sync if items change externally
  useEffect(() => {
    if (!value || value.length === 0) {
      setOrderedItems(items);
    }
  }, [items]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setOrderedItems((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);
        
        // Notify parent of new order
        onChange(newItems.map(i => i.id));
        
        return newItems;
      });
    }
  };

  return (
    <div className="space-y-2">
      <p className="text-sm text-muted-foreground mb-4">
        Ordnen Sie die folgenden Prioritäten nach Wichtigkeit (ziehen zum Sortieren):
      </p>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={orderedItems.map(i => i.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2">
            {orderedItems.map((item, index) => (
              <SortableItem key={item.id} item={item} index={index} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}
